import { Button, Form, Icon, Loader, Message } from 'semantic-ui-react'
import * as React from 'react'

import { profileSchema, techSchema } from '../../types'
import { useTalentApi } from '../../store/mainContext'
import { validateResponse } from '../../utils'
import { useToasts } from '../../components/toasts/ToastsProvider'

type MetadataCollection = 'techs' | 'profiles'

type MetadataFormState = {
  text: string
}

interface Props {
  open: boolean
  collection: MetadataCollection
  itemId?: string
  title: string
  onClose(): void
  onSaved(): void
  onDeleted(): void
}

const schemaByCollection = {
  techs: techSchema,
  profiles: profileSchema,
} as const

const createBlankFormState = (): MetadataFormState => ({
  text: '',
})

export default function SimpleMetadataSidePanel(props: Props) {
  const { open, collection, itemId, title, onClose, onSaved, onDeleted } = props

  const api = useTalentApi()
  const { addToast } = useToasts()

  const isNew = !itemId

  const [formState, setFormState] = React.useState<MetadataFormState | null>(
    null,
  )
  const [baseline, setBaseline] = React.useState<MetadataFormState | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setFormState(null)
      setBaseline(null)
      setError(null)
      setIsLoading(false)
      setIsSaving(false)
      setIsDeleting(false)
      return
    }

    if (!itemId) {
      const blank = createBlankFormState()
      setFormState(blank)
      setBaseline(blank)
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    api
      .get(`${collection}/${itemId}`)
      .then(validateResponse(schemaByCollection[collection]))
      .then((item) => {
        const state = { text: item.text || '' }
        setFormState(state)
        setBaseline(state)
      })
      .catch((err) => {
        console.error(err)
        setError(`Unable to load ${title.toLowerCase()} details.`)
      })
      .finally(() => setIsLoading(false))
  }, [api, collection, itemId, open, title])

  const isDirty = React.useMemo(() => {
    if (!baseline || !formState) return false
    return baseline.text !== formState.text
  }, [baseline, formState])

  const canSave = Boolean(formState) && (isNew || isDirty)

  const handleSave = async () => {
    if (!formState) return

    const payload = { text: formState.text.trim() }

    if (!payload.text) {
      setError('Name is required.')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      if (isNew) {
        await api.post(collection, payload)
        addToast(`${title} created`, { variant: 'success' })
      } else {
        await api.patch(`${collection}/${itemId}`, payload)
        addToast(`${title} updated`, { variant: 'success' })
      }
      onSaved()
    } catch (err) {
      console.error(err)
      setError(`Failed to save ${title.toLowerCase()}.`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!itemId) return

    const confirmed = window.confirm(
      `Are you sure you want to delete this ${title.toLowerCase()}? This action cannot be undone.`,
    )

    if (!confirmed) return

    setIsDeleting(true)
    setError(null)

    try {
      await api.delete(`${collection}/${itemId}`)
      addToast(`${title} deleted`, { variant: 'success' })
      onDeleted()
    } catch (err) {
      console.error(err)
      setError(`Failed to delete ${title.toLowerCase()}.`)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (isSaving || isDeleting || !canSave) return

    handleSave()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/60"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <Message negative>
              <Message.Header>Something went wrong</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <div className="flex justify-between">
            <h1 className="text-xl font-semibold text-slate-900">
              {isNew ? `Add ${title}` : `Edit ${title}`}
            </h1>
            <Button
              icon
              basic
              size="small"
              aria-label={`Close ${title.toLowerCase()} panel`}
              onClick={onClose}
              disabled={isSaving || isDeleting}
            >
              <Icon name="close" />
            </Button>
          </div>

          {isLoading || !formState ? (
            <div className="flex h-full items-center justify-center py-10">
              <Loader active inline="centered" />
            </div>
          ) : (
            <Form
              onSubmit={handleSubmit}
              autoComplete="off"
              loading={isSaving}
              className="flex h-full flex-col"
            >
              <div className="flex flex-col gap-8">
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    {title} Details
                  </h2>

                  <Form.Input
                    label="Name"
                    value={formState.text}
                    onChange={(_, data) =>
                      setFormState((prev) =>
                        prev
                          ? { ...prev, text: String(data.value ?? '') }
                          : prev,
                      )
                    }
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between border-t pt-4">
                <div>
                  {!isNew && (
                    <Button
                      type="button"
                      basic
                      color="red"
                      onClick={handleDelete}
                      disabled={isSaving || isDeleting}
                      loading={isDeleting}
                    >
                      Delete
                    </Button>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    basic
                    onClick={onClose}
                    disabled={isSaving || isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    primary
                    disabled={!canSave || isSaving || isDeleting}
                    loading={isSaving}
                  >
                    {isNew ? `Create ${title}` : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  )
}
